import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import {
    CheckCircle2,
    ArrowLeft,
    FileText,
    Download,
    ExternalLink,
    ChevronRight,
    BookOpen,
    Lock,
    Zap,
    Award
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import SecureVideoPlayer from '@/components/SecureVideoPlayer';

interface Course {
    id: string;
    title: string;
    description?: string;
    slug?: string;
    status?: string;
    thumbnail?: string;
}

interface Module {
    id: string;
    title: string;
    order_index: number;
}

interface Lesson {
    id: string;
    module_id: string;
    title: string;
    duration?: string;
    order_index: number;
    video_url: string;
    stream_video_id?: string;
    description?: string;
}

interface LessonAsset {
    id: string;
    title: string;
    file_url?: string;
    fileUrl?: string; // Support for both camelCase and snake_case
    type: string;
}

interface LessonProgress {
    lesson_id: string;
    is_completed: boolean;
    progress_seconds?: number;
    last_watched_at?: string;
}

const CoursePlayer = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [course, setCourse] = useState<Course | null>(null);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
    const [lessonAssets, setLessonAssets] = useState<LessonAsset[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [completedLessons, setCompletedLessons] = useState<string[]>([]);
    const [hasAccess, setHasAccess] = useState(false);
    const [modules, setModules] = useState<Module[]>([]);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [lastSavedTime, setLastSavedTime] = useState(0);

    // Video protection and global listeners are now managed within the VideoPlayer component.

    const fetchLessonAssets = async (lessonId: string) => {
        const assets = await safeFetch<LessonAsset[]>(
            supabase.from('lesson_assets').select('*').eq('lesson_id', lessonId),
            [],
            'Fetching lesson assets'
        );
        setLessonAssets(assets);
    };

    useEffect(() => {
        const initPlayer = async () => {
            if (!courseId || !user) {
                setIsLoading(false);
                return;
            }

            try {
                // 0. Resolve Slug to ID if necessary
                let realCourseId = courseId;
                const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(courseId);

                if (!isUUID) {
                    const { data: cData } = await supabase.from('courses').select('id').eq('slug', courseId).maybeSingle();
                    if (cData) {
                        realCourseId = cData.id;
                    } else {
                        const { data: cDataById } = await supabase.from('courses').select('id').eq('id', courseId).maybeSingle();
                        if (cDataById) {
                            realCourseId = cDataById.id;
                        } else {
                            setIsLoading(false);
                            return;
                        }
                    }
                }

                // 1. Check Access from Supabase (Optimized Query)
                const { data: enrollment, error: enrollError } = await supabase
                    .from('enrollments')
                    .select('id, progress')
                    .eq('user_id', user.id)
                    .eq('course_id', realCourseId)
                    .maybeSingle();

                if (enrollError) throw enrollError;

                if (!enrollment && user.role !== 'admin') {
                    setHasAccess(false);
                    setIsLoading(false);
                    return;
                }

                setHasAccess(true);

                // 2. Fetch Course & Modules FIRST
                const { data: courseData, error: courseError } = await supabase
                    .from('courses')
                    .select('*')
                    .eq('id', realCourseId)
                    .single();

                if (courseError) throw courseError;
                setCourse(courseData);

                const { data: modulesData, error: modulesError } = await supabase
                    .from('course_modules')
                    .select('*')
                    .eq('course_id', realCourseId)
                    .order('order_index', { ascending: true });

                if (modulesError) throw modulesError;
                setModules(modulesData || []);

                // 3. Fetch Lessons
                const { data: lessonsData, error: lessonsError } = await supabase
                    .from('lessons')
                    .select('id, module_id, title, duration, order_index, video_url, stream_video_id, description')
                    .eq('course_id', realCourseId)
                    .order('order_index', { ascending: true });

                if (lessonsError) throw lessonsError;
                setLessons(lessonsData || []);

                // 4. Fetch User Progress
                const { data: progressData, error: progressError } = await supabase
                    .from('lesson_progress')
                    .select('lesson_id, is_completed, progress_seconds')
                    .eq('user_id', user.id)
                    .eq('course_id', realCourseId);

                if (progressError) throw progressError;

                const completed = progressData ? (progressData as LessonProgress[]).filter(p => p.is_completed).map((p: LessonProgress) => p.lesson_id) : [];
                setCompletedLessons(completed);

                if (lessonsData && lessonsData.length > 0) {
                    // 5. Find Last Watched Point (Attempt RPC but fallback to progressData)
                    let startLesson: Lesson = lessonsData[0];
                    let resumeTime = 0;

                    try {
                        const { data: lastWatched, error: rpcError } = await supabase.rpc('get_last_watched_lesson', {
                            p_user_id: user.id,
                            p_course_id: realCourseId
                        });

                        if (rpcError) {
                            console.warn('RPC Sync Warning (Non-Critical):', rpcError.message);
                            // Fallback logic continues below if lastWatched is empty
                        }

                        if (lastWatched && lastWatched.length > 0) {
                            const found = lessonsData.find((l: Lesson) => l.id === lastWatched[0].lesson_id);
                            if (found) {
                                startLesson = found;
                                resumeTime = lastWatched[0].progress_seconds || 0;
                                if (import.meta.env.DEV) console.log(`--- RESUME: ${startLesson.title} AT ${resumeTime}s ---`);
                            }
                        } else if (progressData && progressData.length > 0) {
                            // Fallback: If RPC returns nothing but we have progress data, find the latest one
                            const progressTyped = progressData as LessonProgress[];
                            const latestWithProgress = progressTyped.reduce((prev: LessonProgress, curr: LessonProgress) => {
                                if (!curr.last_watched_at) return prev;
                                if (!prev.last_watched_at) return curr;
                                return (curr.last_watched_at > prev.last_watched_at) ? curr : prev;
                            }, progressTyped[0]);

                            const found = lessonsData.find((l: Lesson) => l.id === latestWithProgress.lesson_id);
                            if (found) {
                                startLesson = found;
                                resumeTime = latestWithProgress.progress_seconds || 0;
                            }
                        }
                    } catch (rpcCatch) {
                        console.error('RPC Execution Error:', rpcCatch);
                    }

                    setActiveLesson(startLesson);
                    setLastSavedTime(resumeTime);
                }

            } catch (error) {
                console.error('Player Init Error:', error);
                toast.error('Failed to synchronize course terminal');
            } finally {
                setIsLoading(false);
            }
        };

        initPlayer();
    }, [courseId, user]);

    useEffect(() => {
        if (activeLesson) {
            fetchLessonAssets(activeLesson.id);
        }
    }, [activeLesson]);

    const markAsCompleted = async (lessonId: string) => {
        if (!user || !course?.id || completedLessons.includes(lessonId)) return;

        try {
            const { error } = await supabase
                .from('lesson_progress')
                .upsert({
                    user_id: user.id,
                    course_id: course.id,
                    lesson_id: lessonId,
                    is_completed: true,
                    progress_percent: 100,
                    last_watched_at: new Date().toISOString()
                }, { onConflict: 'user_id,lesson_id' });

            if (error) throw error;

            const newCompleted = [...completedLessons, lessonId];
            setCompletedLessons(newCompleted);
            toast.success('Node Synchronized');

            // Auto move to next lesson
            setTimeout(nextLesson, 1500);

        } catch (error) {
            toast.error('Failed to update mastery nodes');
        }
    };

    const handleTimeUpdate = async (currentTimeRaw: number, durationRaw: number) => {
        if (!activeLesson || !user || !course?.id) return;

        const currentTime = Math.floor(currentTimeRaw);
        const duration = Math.floor(durationRaw);

        // Save progress every 10 seconds or when completing
        if (currentTime > 0 && (currentTime % 10 === 0) && currentTime !== lastSavedTime) {
            setLastSavedTime(currentTime);
            const progressPercent = Math.round((currentTime / (duration || 1)) * 100);

            await supabase
                .from('lesson_progress')
                .upsert({
                    user_id: user.id,
                    course_id: course.id,
                    lesson_id: activeLesson.id,
                    progress_seconds: currentTime,
                    duration_seconds: duration,
                    progress_percent: progressPercent,
                    last_watched_at: new Date().toISOString()
                }, { onConflict: 'user_id,lesson_id' });
        }
    };

    const handleLoadedMetadata = () => {
        // Resume logic is now handled internally by VideoPlayer via startTime prop
    };

    const nextLesson = () => {
        if (!activeLesson || lessons.length === 0) return;
        const currentIndex = lessons.findIndex((l: Lesson) => l.id === activeLesson.id);
        if (currentIndex < lessons.length - 1) {
            setActiveLesson(lessons[currentIndex + 1]);
        }
    };


    if (isLoading) return <Layout><div className="section-padding text-center">Configuring Educational Cluster...</div></Layout>;

    if (!hasAccess) {
        return (
            <Layout>
                <div className="section-padding flex items-center justify-center min-h-[70vh]">
                    <div className="card-premium max-w-lg w-full p-12 text-center bg-white/40 border-white/60">
                        <div className="w-20 h-20 rounded-full bg-destructive/5 flex items-center justify-center mx-auto mb-8 border border-destructive/10">
                            <Lock className="w-8 h-8 text-destructive" />
                        </div>
                        <h2 className="text-3xl font-black text-primary uppercase tracking-tighter mb-4">Mastery Lock Active</h2>
                        <p className="text-muted-foreground font-medium mb-10">Your profile lacks the required permissions for this learning cluster.</p>
                        <div className="flex flex-col gap-4">
                            <Link to="/courses">
                                <Button className="w-full btn-gold h-14 text-xs font-black uppercase tracking-widest shadow-premium">
                                    Initiate Enrollment
                                </Button>
                            </Link>
                            <Link to="/my-courses" className="text-[10px] font-black uppercase tracking-widest text-primary/40 hover:text-primary transition-colors">
                                Return to Vault
                            </Link>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!course || !activeLesson) return <Layout><div className="section-padding text-center">Course terminal not found.</div></Layout>;

    const progress = Math.round((completedLessons.length / lessons.length) * 100);

    return (
        <Layout>
            <div className="bg-background min-h-screen relative overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] z-0" />

                <div className="container mx-auto px-6 py-12 select-none">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
                        <div className="flex flex-col gap-2">
                            <Link to="/my-courses" className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40 hover:text-accent transition-colors mb-2">
                                <ArrowLeft className="w-4 h-4" />
                                RETURN TO VAULT
                            </Link>
                            <h2 className="text-2xl md:text-3xl font-black text-primary uppercase tracking-tighter">
                                {course.title}
                            </h2>
                        </div>
                        <div className="flex items-center gap-4 bg-white/40 backdrop-blur-xl p-4 rounded-2xl border border-white/60">
                            <div className="flex flex-col items-end">
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">MASTERY STATUS</span>
                                <span className="text-xs font-black text-primary">{progress}% COMPLETE</span>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center border border-accent/10">
                                <Award className="w-6 h-6 text-accent" />
                            </div>
                        </div>
                    </div>

                    <div className="course-layout flex flex-col lg:flex-row gap-[30px] items-start">
                        {/* Video Section (70%) */}
                        <div className="video-section flex-[7] space-y-10 w-full lg:w-0">
                            <div className="space-y-10">
                                <SecureVideoPlayer
                                    ref={videoRef}
                                    videoUrl={activeLesson.video_url || ''}
                                    streamVideoId={activeLesson.stream_video_id}
                                    userEmail={user?.email || ''}
                                    startTime={lastSavedTime}
                                    overlayImageUrl="/overlay-image.png"
                                    onTimeUpdate={handleTimeUpdate}
                                    onLoadedMetadata={handleLoadedMetadata}
                                    onEnded={() => markAsCompleted(activeLesson.id)}
                                />
                            </div>

                            <div className="flex flex-col gap-8 p-10 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-premium">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="max-w-xl">
                                        <div className="flex flex-col gap-2">
                                            <h1 className="text-3xl font-black text-primary uppercase tracking-tighter leading-none">
                                                {activeLesson.title}
                                            </h1>
                                            <p className="text-[10px] font-black text-accent uppercase tracking-[0.2em]">{activeLesson.duration || 'N/A'} Duration</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={() => markAsCompleted(activeLesson.id)}
                                            disabled={completedLessons.includes(activeLesson.id)}
                                            className={cn(
                                                "rounded-2xl h-14 px-8 text-[9px] font-black uppercase tracking-widest transition-all duration-500 shadow-premium",
                                                completedLessons.includes(activeLesson.id)
                                                    ? "bg-green-500 text-white hover:bg-green-600"
                                                    : "btn-gold"
                                            )}
                                        >
                                            {completedLessons.includes(activeLesson.id) ? (
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    Synchronized
                                                </div>
                                            ) : 'Mark Completed'}
                                        </Button>
                                        <Button
                                            onClick={nextLesson}
                                            variant="outline"
                                            className="rounded-2xl h-14 w-14 p-0 border-primary/10 hover:bg-primary/5 shadow-sm"
                                            disabled={lessons.findIndex((l: Lesson) => l.id === activeLesson.id) === lessons.length - 1}
                                        >
                                            <ChevronRight className="w-5 h-5 text-primary" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Intellectual & Support Documents grouped in 70% section */}
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-8">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-2xl bg-primary/5 flex items-center justify-center border border-primary/10">
                                            <Zap className="w-5 h-5 text-primary" />
                                        </div>
                                        <h3 className="text-xl font-black text-primary uppercase tracking-tighter">Transmission Intel</h3>
                                    </div>
                                    <div className="p-8 bg-white/40 backdrop-blur-xl border border-white/60 rounded-[2rem] shadow-premium h-full">
                                        <p className="text-muted-foreground leading-relaxed font-medium">
                                            {activeLesson.description || 'No description available for this module.'}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-2xl bg-accent/5 flex items-center justify-center border border-accent/10">
                                            <FileText className="w-5 h-5 text-accent" />
                                        </div>
                                        <h3 className="text-xl font-black text-primary uppercase tracking-tighter">Support Documents</h3>
                                    </div>
                                    <div className="grid gap-6">
                                        {lessonAssets.length === 0 ? (
                                            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground/40 p-10 border border-dashed rounded-[1.5rem] border-primary/10 text-center">No external assets for this node.</p>
                                        ) : lessonAssets.map((resource: LessonAsset) => (
                                            <a
                                                key={resource.id}
                                                href={resource.file_url || resource.fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-between p-6 bg-white/40 backdrop-blur-xl border border-white/60 rounded-[1.5rem] hover:bg-white hover:shadow-premium transition-all duration-500 group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-500">
                                                        <FileText className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-primary uppercase tracking-tighter group-hover:text-accent transition-colors">{resource.title}</p>
                                                        <p className="text-[8px] font-bold text-muted-foreground uppercase">{resource.type}</p>
                                                    </div>
                                                </div>
                                                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-primary/5 transition-colors">
                                                    {resource.type === 'link' ? <ExternalLink className="w-4 h-4 text-primary/40" /> : <Download className="w-4 h-4 text-primary/40" />}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modules Section (30%) */}
                        <div className="modules-section flex-[3] sticky top-[100px] h-fit w-full lg:w-0">
                            <div className="bg-white/60 backdrop-blur-2xl border border-white/80 rounded-[2.5rem] overflow-hidden shadow-premium">
                                <div className="p-8 border-b border-gray-100 bg-white/40">
                                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-accent mb-2 block">Learning Sequence</span>
                                    <h3 className="text-xl font-black text-primary uppercase tracking-tighter">Transmission Modules</h3>
                                    <div className="flex items-center gap-4 mt-4">
                                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                                            <BookOpen className="w-3 h-3" />
                                            {lessons.length} ACTIVE NODES
                                        </div>
                                    </div>
                                </div>
                                <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                                    {modules.length > 0 ? (
                                        modules.map((module) => (
                                            <div key={module.id} className="border-b border-gray-50 last:border-0">
                                                <div className="bg-slate-50/50 px-6 py-3 border-y border-gray-100/50">
                                                    <h4 className="text-[10px] font-black text-primary/40 uppercase tracking-widest">{module.title}</h4>
                                                </div>
                                                {lessons.filter(l => l.module_id === module.id).map((lesson, idx) => (
                                                    <button
                                                        key={lesson.id}
                                                        onClick={() => setActiveLesson(lesson)}
                                                        className={cn(
                                                            "w-full flex items-start gap-5 p-6 text-left transition-all duration-500",
                                                            activeLesson.id === lesson.id ? "bg-white border-l-[6px] border-l-accent" : "hover:bg-white/80"
                                                        )}
                                                    >
                                                        <div className="mt-1">
                                                            {completedLessons.includes(lesson.id) ? (
                                                                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center border border-white shadow-sm">
                                                                    <CheckCircle2 className="w-3 h-3 text-white" />
                                                                </div>
                                                            ) : (
                                                                <div className={cn(
                                                                    "w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors duration-500",
                                                                    activeLesson.id === lesson.id ? "bg-accent border-accent text-accent-foreground" : "border-gray-200 text-gray-300"
                                                                )}>
                                                                    <span className="text-[7px] font-black">{lesson.order_index}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={cn(
                                                                "text-[10px] font-black uppercase tracking-tight leading-snug mb-1 transition-colors duration-500",
                                                                activeLesson.id === lesson.id ? "text-primary" : "text-primary/60"
                                                            )}>
                                                                {lesson.title}
                                                            </p>
                                                            <span className="text-[8px] font-bold text-muted-foreground uppercase">{lesson.duration || 'N/A'}</span>
                                                        </div>
                                                        {activeLesson.id === lesson.id && <ChevronRight className="w-4 h-4 text-accent mt-1" />}
                                                    </button>
                                                ))}
                                            </div>
                                        ))
                                    ) : (
                                        lessons.map((lesson: Lesson, index: number) => (
                                            <button
                                                key={lesson.id}
                                                onClick={() => setActiveLesson(lesson)}
                                                className={cn(
                                                    "w-full flex items-start gap-5 p-6 text-left transition-all duration-500 border-b border-gray-50 last:border-0",
                                                    activeLesson.id === lesson.id ? "bg-white border-l-[6px] border-l-accent" : "hover:bg-white/80"
                                                )}
                                            >
                                                <div className="mt-1">
                                                    {completedLessons.includes(lesson.id) ? (
                                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center border border-white shadow-sm">
                                                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                                        </div>
                                                    ) : (
                                                        <div className={cn(
                                                            "w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors duration-500",
                                                            activeLesson.id === lesson.id ? "bg-accent border-accent text-accent-foreground" : "border-gray-200 text-gray-300"
                                                        )}>
                                                            <span className="text-[8px] font-black">{index + 1}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={cn(
                                                        "text-[10px] font-black uppercase tracking-tight leading-snug mb-1 transition-colors duration-500",
                                                        activeLesson.id === lesson.id ? "text-primary" : "text-primary/60"
                                                    )}>
                                                        {lesson.title}
                                                    </p>
                                                    <span className="text-[8px] font-bold text-muted-foreground uppercase">{lesson.duration || 'N/A'}</span>
                                                </div>
                                                {activeLesson.id === lesson.id && <ChevronRight className="w-4 h-4 text-accent mt-1" />}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CoursePlayer;
